import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation, useParams } from "react-router-dom";
import axios from 'axios';

function formatCurrency(value, currency) {
  const safeCurrency = currency && String(currency).trim().length === 3 ? String(currency).trim().toUpperCase() : 'EUR';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: safeCurrency,
    minimumFractionDigits: 2,
  }).format(Number(value || 0));
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatStatus(status) {
  return String(status || 'DRAFT').replace(/_/g, ' ');
}

function getStatusClass(status) {
  switch (status) {
    case 'ISSUED': return 'iv-status iv-status--issued';
    case 'PAID': return 'iv-status iv-status--paid';
    case 'OVERDUE': return 'iv-status iv-status--overdue';
    case 'PARTIALLY_PAID': return 'iv-status iv-status--partial';
    case 'VOID': return 'iv-status iv-status--void';
    default: return 'iv-status iv-status--draft';
  }
}

function getLifecycleMessage(status) {
  switch (status) {
    case 'ISSUED':
    case 'OVERDUE':
      return 'Issued invoices can still be updated if you need to correct the draft before settlement. Paid and partially paid invoices remain locked.';
    case 'PARTIALLY_PAID':
      return 'Partially paid invoices are locked to protect financial history. You can still record the remaining payments.';
    case 'PAID':
    case 'VOID':
      return 'Paid and void invoices are locked to preserve financial history.';
    default:
      return 'Draft invoices are fully editable until they are issued.';
  }
}

export default function InvoiceDocumentPage() {
  useTranslation();
  const auth = useSelector((state) => state.auth);
  const history = useNavigate();
  const location = useLocation();
  const { invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const hasAutoPrinted = useRef(false);

  const requestConfig = useMemo(() => ({
    headers: {
      Authorization: `Bearer ${auth.token}`,
      'Content-Type': 'application/json',
    },
  }), [auth.token]);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const shouldAutoPrint = searchParams.get('autoprint') === '1';

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [detailRes, settingsRes] = await Promise.all([
        axios.post('/api/invoice/details', JSON.stringify({
          workspace_id: auth.workspace_id,
          invoice_id: invoiceId,
        }), requestConfig),
        axios.get(`/api/invoice/settings?workspace_id=${auth.workspace_id}`, requestConfig),
      ]);
      setInvoice(detailRes.data.invoice);
      setSettings(settingsRes.data.settings || {});
    } catch (err) {
      setError('Failed to load invoice.');
    } finally {
      setLoading(false);
    }
  }, [auth.workspace_id, invoiceId, requestConfig]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!shouldAutoPrint || loading || !invoice || hasAutoPrinted.current) return;
    hasAutoPrinted.current = true;
    const timer = window.setTimeout(() => window.print(), 350);
    return () => window.clearTimeout(timer);
  }, [invoice, loading, shouldAutoPrint]);

  const totals = useMemo(() => {
    if (!invoice) return { subtotal: 0, tax: 0, total: 0 };
    return {
      subtotal: Number(invoice.subtotal || 0),
      tax: Number(invoice.tax_total || 0),
      total: Number(invoice.total || 0),
    };
  }, [invoice]);

  const balanceDue = invoice ? Math.max(0, totals.total - Number(invoice.amount_paid || 0)) : 0;

  const senderLines = useMemo(() => {
    if (!settings) return [];
    return [
      settings.legal_name,
      settings.address_line1,
      settings.address_line2,
      [settings.city, settings.postal_code].filter(Boolean).join(' '),
      settings.country,
      settings.sender_email,
      settings.sender_phone,
    ].filter(Boolean);
  }, [settings]);

  const clientLines = useMemo(() => {
    if (!invoice) return [];
    return [
      invoice.snapshot_company_name || invoice.snapshot_client_name,
      invoice.snapshot_billing_contact,
      invoice.snapshot_billing_email,
      invoice.snapshot_address,
      invoice.snapshot_vat_number ? `VAT: ${invoice.snapshot_vat_number}` : null,
      invoice.snapshot_tax_id ? `Tax ID: ${invoice.snapshot_tax_id}` : null,
    ].filter(Boolean);
  }, [invoice]);

  return (
    <div className='ivdoc-standalone'>
      <div className='ivd-shell'>
            <div className='ivd-topbar'>
              <button type='button' className='cp-back' onClick={() => history(`/invoices/${invoiceId}`)}>{tr("Back to Invoice")}</button>
              <div className='cp-actions'>
                <button type='button' className='cp-btn cp-btn--secondary' onClick={() => window.print()}>{tr("Print / Save PDF")}</button>
              </div>
            </div>

            {error && <div className='iv-error'>{error}</div>}

            {loading ? (
              <div className='cp-loading-card'>{tr('Loading invoice...')}</div>
            ) : invoice ? (
              <div className='ivd-layout'>
                <div className='ivd-doc'>
                  <div className='ivd-header'>
                    <div className='ivd-sender'>
                      {senderLines.length > 0 ? (
                        senderLines.map((line, i) => <p key={i}>{line}</p>)
                      ) : (
                        <p className='ivd-muted'>Configure your sender details in Invoice Settings.</p>
                      )}
                    </div>
                    <div className='ivd-header-right'>
                      <h1 className='ivd-title'>INVOICE</h1>
                      <div className='ivd-number'>{invoice.invoice_number}</div>
                      <span className={getStatusClass(invoice.status)}>{formatStatus(invoice.status)}</span>
                      <p className='ivd-locked-note'>{getLifecycleMessage(invoice.status)}</p>
                    </div>
                  </div>

                  <div className='ivd-meta-grid'>
                    <div className='ivd-bill-to'>
                      <div className='ivd-meta-label'>Bill To</div>
                      {clientLines.length > 0 ? (
                        clientLines.map((line, i) => <p key={i}>{line}</p>)
                      ) : (
                        <p className='ivd-muted'>No billing profile saved for this client.</p>
                      )}
                    </div>
                    <div className='ivd-dates'>
                      <div className='ivd-date-row'>
                        <span>Issue Date</span>
                        <strong>{formatDate(invoice.issue_date)}</strong>
                      </div>
                      <div className='ivd-date-row'>
                        <span>Due Date</span>
                        <strong>{formatDate(invoice.due_date)}</strong>
                      </div>
                      {invoice.service_period_start && (
                        <div className='ivd-date-row'>
                          <span>Service Period</span>
                          <strong>{formatDate(invoice.service_period_start)} – {formatDate(invoice.service_period_end)}</strong>
                        </div>
                      )}
                      <div className='ivd-date-row'>
                        <span>Currency</span>
                        <strong>{invoice.currency}</strong>
                      </div>
                    </div>
                  </div>

                  <table className='ivd-table'>
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th className='ivd-th-num'>Qty</th>
                        <th className='ivd-th-num'>Unit</th>
                        <th className='ivd-th-num'>Unit Price</th>
                        <th className='ivd-th-num'>Tax %</th>
                        <th className='ivd-th-num'>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(invoice.lineItems || []).map((item) => (
                        <tr key={item.id}>
                          <td>{item.description}</td>
                          <td className='ivd-td-num'>{Number(item.quantity)}</td>
                          <td className='ivd-td-num'>{item.unit || 'hrs'}</td>
                          <td className='ivd-td-num'>{formatCurrency(item.unit_price, invoice.currency)}</td>
                          <td className='ivd-td-num'>{Number(item.tax_rate)}%</td>
                          <td className='ivd-td-num'>{formatCurrency(item.line_total, invoice.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className='ivd-totals-wrap'>
                    <div className='ivd-totals'>
                      <div className='ivd-total-row'>
                        <span>Subtotal</span>
                        <strong>{formatCurrency(totals.subtotal, invoice.currency)}</strong>
                      </div>
                      <div className='ivd-total-row'>
                        <span>Tax</span>
                        <strong>{formatCurrency(totals.tax, invoice.currency)}</strong>
                      </div>
                      <div className='ivd-total-row ivd-total-grand'>
                        <span>Total</span>
                        <strong>{formatCurrency(totals.total, invoice.currency)}</strong>
                      </div>
                      {Number(invoice.amount_paid) > 0 && (
                        <div className='ivd-total-row'>
                          <span>Amount Paid</span>
                          <strong>{formatCurrency(invoice.amount_paid, invoice.currency)}</strong>
                        </div>
                      )}
                      <div className='ivd-total-row ivd-total-due'>
                        <span>Balance Due</span>
                        <strong>{formatCurrency(balanceDue, invoice.currency)}</strong>
                      </div>
                    </div>
                  </div>

                  {(invoice.payment_instructions || invoice.notes) && (
                    <div className='ivd-notes-grid'>
                      {invoice.payment_instructions && (
                        <div className='ivd-notes-block'>
                          <div className='ivd-meta-label'>Payment Instructions</div>
                          <p>{invoice.payment_instructions}</p>
                        </div>
                      )}
                      {invoice.notes && (
                        <div className='ivd-notes-block'>
                          <div className='ivd-meta-label'>Notes</div>
                          <p>{invoice.notes}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {settings?.footer_notes && (
                    <div className='ivd-footer-notes'>{settings.footer_notes}</div>
                  )}
                </div>
              </div>
            ) : null}
      </div>
    </div>
  );
}
